<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <a-table
        :columns="columns"
        :data-source="items"
        :row-key="'id'"
        :loading="loading"
        :pagination="pagination"
        :row-selection="rowSelection"
        :scroll="{ x: 1260 }"
        class="text-nowrap align-middle stock-report-table"
        @change="handleChange"
      >
        <template #emptyText>
          <div class="py-4 text-center text-muted">{{ $t(`${i18nScope}.no_stock_reports_found`) }}</div>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column && column.key === 'type'">
            <span class="type-badge" :class="Number(record.type) === 1 ? 'type-standard' : 'type-service'">
              {{ Number(record.type) === 1 ? $t(`${i18nScope}.type_standard`) : $t(`${i18nScope}.type_service`) }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'cost'">
            ${{ formatAmount(record.cost) }}
          </template>

          <template v-else-if="column && column.key === 'price'">
            ${{ formatAmount(record.price) }}
          </template>

          <template v-else-if="column && column.key === 'stock'">
            <span class="qty-chip">
              {{ Number(record.instock_qty ?? 0) }}{{ record.unit ? ` ${record.unit}` : "" }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'status'">
            <span class="status-badge" :class="getStatusClass(record)">
              {{ getStatusLabel(record) }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'total_cost'">
            {{ Number(record.type) === 1 ? `$${formatAmount(Number(record.instock_qty ?? 0) * Number(record.cost ?? 0))}` : "-" }}
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import type { TablePaginationConfig } from "ant-design-vue";
import type { InventoryReportItem } from "@/services/report";

export default defineComponent({
  name: "StockReport",
  props: {
    items: {
      type: Array as () => InventoryReportItem[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object as () => TablePaginationConfig,
      required: true,
    },
    selectedProductIds: {
      type: Array as () => number[],
      default: () => [],
    },
    i18nScope: {
      type: String,
      default: "stock_report_page",
    },
  },
  emits: ["table-change", "selection-change"],
  setup(props, { emit }) {
    const { t, locale } = useI18n();

    const columns = computed(() => [
      { title: t(`${props.i18nScope}.table_code`), key: "sku", dataIndex: "sku", width: 150 },
      { title: t(`${props.i18nScope}.table_product`), key: "product_name", dataIndex: "product_name", width: 220 },
      { title: t(`${props.i18nScope}.table_category`), key: "category_name", dataIndex: "category_name", width: 180 },
      { title: t(`${props.i18nScope}.table_brand`), key: "brand_name", dataIndex: "brand_name", width: 170 },
      { title: t(`${props.i18nScope}.table_unit`), key: "unit", dataIndex: "unit", width: 110, align: "center" as const },
      { title: t(`${props.i18nScope}.table_type`), key: "type", dataIndex: "type", width: 130, align: "center" as const },
      { title: t(`${props.i18nScope}.table_cost`), key: "cost", dataIndex: "cost", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_price`), key: "price", dataIndex: "price", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_current_stock`), key: "stock", dataIndex: "instock_qty", width: 150, align: "center" as const },
      { title: t(`${props.i18nScope}.table_stock_status`), key: "status", width: 150, align: "center" as const },
      { title: t(`${props.i18nScope}.table_total_cost`), key: "total_cost", width: 140, align: "center" as const },
    ]);

    const rowSelection = computed(() => ({
      selectedRowKeys: props.selectedProductIds,
      onChange: (selectedRowKeys: (string | number)[]) => {
        emit("selection-change", selectedRowKeys.map((id) => Number(id)));
      },
    }));

    const formatAmount = (value?: string | number) =>
      Number(value ?? 0).toLocaleString(locale.value === "km" ? "km-KH" : "en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    const getStatusLabel = (item: InventoryReportItem) => {
      const qty = Number(item.instock_qty ?? 0);
      const alert = Number(item.qty_alert ?? 0);
      if (qty <= 0) return t(`${props.i18nScope}.status_out_of_stock`);
      if (alert > 0 && qty <= alert) return t(`${props.i18nScope}.status_low_stock`);
      return t(`${props.i18nScope}.status_in_stock`);
    };

    const getStatusClass = (item: InventoryReportItem) => {
      const qty = Number(item.instock_qty ?? 0);
      const alert = Number(item.qty_alert ?? 0);
      if (qty <= 0) return "status-out";
      if (alert > 0 && qty <= alert) return "status-low";
      return "status-in";
    };

    const handleChange = (pagination: TablePaginationConfig) => {
      emit(
        "table-change",
        pagination.current || 1,
        pagination.pageSize === 1000000 ? "all" : pagination.pageSize || 10
      );
    };

    return {
      columns,
      rowSelection,
      formatAmount,
      getStatusLabel,
      getStatusClass,
      handleChange,
    };
  },
});
</script>

<style scoped>
.stock-report-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.stock-report-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
}

.type-badge,
.qty-chip,
.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.type-standard {
  color: #1f9254;
  background: #e8f7ee;
}

.type-service {
  color: #5d5fef;
  background: rgba(93, 95, 239, 0.12);
}

.qty-chip {
  color: #1f2940;
  background: #eef2f7;
}

.status-in {
  color: #1f9254;
  background: #e8f7ee;
}

.status-low {
  color: #d48a00;
  background: #fff4db;
}

.status-out {
  color: #d33c43;
  background: #fdecec;
}
</style>
